import { useState } from "react";
import { useIsMutating, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  buyerSubmissionsApi,
  NO_PREQUALIFICATION_LETTER_CODE,
  OLDER_FORMAT_DOCUMENT_CODE,
  type BuyerPackageAttachmentDto,
  type BuyerPackageAttachmentManifestDto,
  type BuyerSubmissionDocumentDto,
  type BuyerSubmissionDocumentKind,
  type BuyerSubmissionPackageDto,
} from "@/api/buyers";
import { ApiError } from "@/api/client";
import {
  Button,
  Skeleton,
  StatusBadge,
  Textarea,
  Timestamp,
  useToast,
} from "@/design-system/components/ui";
// Modal is a staff-shell component, not part of the shared portal kit.
import { Modal } from "@/components/ui/Modal";
import { ConfirmModal } from "@/components/ui/PromptModals";
import { buyerSubmissionKeys } from "@/hooks/buyers/buyerKeys";
import { PackageLetterOverrideField } from "./PackageLetterOverrideField";
import { ReferralDisclosureControl } from "./submission/ReferralDisclosureControl";
import { InlineQueryError } from "./queryStates";
import {
  SendPreviewModal,
  type SendPreviewSelectableAttachment,
} from "./sendPreview/SendPreviewModal";
import { useSenderIdentity } from "./sendPreview/useSenderIdentity";

/**
 * ONE CARD PER DOCUMENT THE SERVER RETURNS, and no count is written down here.
 *
 * <p>This line read "FIVE documents, five cards" and had been wrong twice over
 * by the time anyone noticed — the buyer's cover letter made it six and the
 * seller brochure makes it seven. The panel never knew a total: it maps
 * `pkg.documents`, so the number is the server's and the prose was decoration
 * that could only ever rot. The same rot reached the header badge, where it was
 * not decoration — it rendered "5 of 4 written" to a specialist.</p>
 *
 * 🔴 Generation is per document and is its OWN button, never a side effect of
 * sending (L32). That is what makes the review step real — a specialist reads a
 * document, fixes a sentence, and sends the thing they read.
 *
 * 🔴 <b>Generation is no longer gated on a prequalification letter, and that is
 * a business rule MOVED rather than removed.</b> Phoenix still does not present
 * a buyer it has not qualified — but that now bites at the SEND, where an absent
 * letter requires an explicit confirmation naming what is missing. It moved
 * because a specialist assembling a package while the buyer finishes the funnel
 * is the normal case, and because what matters is what reaches a listing broker,
 * not what sits in a draft.
 *
 * 🔴 Every gate is SERVER-RESOLVED. A button's enabled state and the server's
 * refusal come from the same read, so they cannot drift into disagreeing. Render
 * the reason you are given; never compose a second one here.
 *
 * <p>🔴 <b>ONE exception, and it is forced.</b> The attachment ceiling is now a
 * question about the TICKED set, and the server cannot answer it before the
 * send — there is nothing to ask about until a specialist has chosen. So
 * {@link selectionBlockedReason} composes that one sentence here, from the
 * server's own `attachmentLimitBytes`, and the server still refuses
 * independently. At rest the two agree by construction: every generated
 * document starts ticked, so the client's sum is the manifest's sum.</p>
 *
 * 🔴 <b>A card offers generate, edit, regenerate, preview and download. A SEND
 * chooses which of those documents travels.</b> The picker lives on the
 * confirmation rather than on the cards because what a package contains is a
 * property of the SEND and not of the document: the same documents are one
 * click apart from going to the listing broker or to the buyer, and there is no
 * reason the two must carry the same subset — they already do not, since the
 * server's `defaultSelectedKinds` leaves the buyer's own cover letter unticked
 * on the broker's send. Tick state is per-send and per-modal, and
 * it is discarded when the modal closes.
 *
 * <p>🔴 There is still no Delete. A document is regenerated, never removed —
 * the card and the picker both act on a set the server resolves, and neither
 * can make a document stop existing.</p>
 *
 * <p>⚠️ This paragraph used to read "there is no Delete and no per-send
 * picker … a document offers generate, edit, regenerate and download". Two of
 * its three claims were false when it was written: there was no per-card
 * download at all — the card offered Preview — and the picker arrived later.
 * A comment that describes a surface is a claim about it.</p>
 */

/**
 * Human labels for the narrative blocks a document may own.
 *
 * The server owns the keys AND decides which document owns which — this map is
 * only how each reads to a specialist, and an editor renders exactly the keys
 * the document's own narrative came back with. The old numbering ("1. Cover
 * letter", "4. Financial capacity") is gone with the ten-section spine it
 * described.
 */
const BLOCK_LABELS: Record<string, string> = {
  coverLetter: "Cover letter",
  executiveSummary: "Executive summary",
  buyerFit: "Why this buyer fits this business",
  financialCapacity: "Financial capacity",
  // No document-3 keys: the Potential Deal Structure has no narrative, and an
  // older-format document 3 refuses editing, so no editor ever shows them.
  interestNarrative: "Buyer interest",
  nextSteps: "What we are asking for",
};

const blockLabel = (key: string) => BLOCK_LABELS[key] ?? key;

export function SubmissionPackagePanel({
  submissionId,
  buyerLeadId,
  prequalificationLetterOverrideSubmissionId,
  canEdit = false,
}: {
  submissionId: string;
  /**
   * The buyer, for the links a card's empty or blocked state needs. The package
   * read does not carry the buyer, but the page rendering this panel already
   * holds it, so it is passed down rather than added to the server's shape.
   */
  buyerLeadId?: string;
  /**
   * Which prequalification letter THIS package encloses, when a specialist has
   * overridden the default. The package read does not carry it — it is a column
   * on the submission — so it arrives from the page like `buyerLeadId`.
   */
  prequalificationLetterOverrideSubmissionId?: string | null;
  /**
   * 🔴 The page's write answer — the same one every sibling section gets.
   *
   * <p>Defaults to `false` rather than `true`: a caller that forgets to pass it
   * should lose an affordance, not gain one. Read stays ungated — a specialist
   * who cannot send still needs to see what exists and when it went out.</p>
   */
  canEdit?: boolean;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [editing, setEditing] = useState<BuyerSubmissionDocumentDto | null>(null);
  const [sending, setSending] = useState(false);
  const [sendingToBuyer, setSendingToBuyer] = useState(false);

  const state = useQuery({
    queryKey: buyerSubmissionKeys.package(submissionId),
    queryFn: () => buyerSubmissionsApi.getPackage(submissionId),
    enabled: Boolean(submissionId),
  });

  const invalidate = () => {
    // The submission's status changes with the package, so the prefix covers
    // the detail read and the list's status column in one invalidation.
    void qc.invalidateQueries({ queryKey: buyerSubmissionKeys.all });
  };

  const writingDocument = useDocumentWriteInFlight(submissionId);

  const generateAll = useMutation({
    mutationKey: buyerSubmissionKeys.packageDocumentWrite(submissionId),
    mutationFn: () => buyerSubmissionsApi.generateAllDocuments(submissionId),
    /*
      🔴 THE TOAST COUNTS WHAT LANDED. `generateAll` skips a blocked document
      and writes the rest, so a flat "All documents generated." is read by
      the one specialist it is false for — the one whose deal structure was never
      completed — at the exact moment they would otherwise notice the gap. It
      names the skipped document rather than the number alone, because "three of
      four" sends the reader hunting for which one.

      🔴 AND IT NAMES NO TOTAL. These strings said "four" while the package
      held five documents and generated four of them; the buyer's letter made it
      six and five, and the seller brochure makes it seven and five. A count
      typed into a label is a fact about the enum that nothing checks — it goes
      stale silently, on the surface a specialist reads most often, and it has
      now gone stale twice on this one panel. The count that matters here is the
      SKIPPED one, which is computed.
    */
    onSuccess: (pkg) => {
      invalidate();
      const skipped = pkg.documents.filter((d) => d.isAuthored && d.blockedReason);
      toast.success(
        skipped.length === 0
          ? "All documents generated."
          : `Generated all but the ${listTitles(skipped)}. See its card for why.`,
      );
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const markSent = useMutation({
    mutationFn: () => buyerSubmissionsApi.markPackageSent(submissionId),
    onSuccess: () => {
      invalidate();
      toast.success("Recorded as sent.");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const markSentToBuyer = useMutation({
    mutationFn: () => buyerSubmissionsApi.markPackageSentToBuyer(submissionId),
    onSuccess: () => {
      invalidate();
      toast.success("Recorded as sent to the buyer.");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (state.isLoading) return <Skeleton className="h-40 w-full" />;
  // 🔴 Was `return null` — the entire panel disappeared on a failed read, so a
  // specialist saw a page with no package section at all and no reason to think
  // one should be there. A vanished surface is the least recoverable of the
  // dishonest states: there is nothing left on screen to retry from.
  if (state.isError) {
    return (
      <section className="grid gap-space-4">
        <header className="flex items-center justify-between">
          <h2 className="text-h3">Submission package</h2>
        </header>
        <InlineQueryError
          what="the submission package"
          onRetry={() => void state.refetch()}
          retrying={state.isFetching}
        />
      </section>
    );
  }
  if (!state.data) return null;

  const pkg = state.data;
  /*
    🔴 BOTH HALVES OF THE FRACTION COUNT THE SAME ARRAY, and the denominator
    is why. It was the literal `4`, typed when there were four authored
    documents — so the buyer's cover letter made the package render
    "5 of 4 written" the moment every document was generated, on the header a
    specialist reads first, with nothing failing.

    The count that is true is the one derived from the read: `isAuthored` is the
    server's own answer to which documents Phoenix WRITES, so the numerator and
    the denominator move together and a seventh kind cannot make them disagree.
    A non-authored kind is in neither — the brochure and the letter are not
    written here, and counting them would report a package as unfinished
    forever.
  */
  const authoredCount = pkg.documents.filter((d) => d.isAuthored).length;
  const generatedCount = pkg.documents.filter(
    (d) => d.isAuthored && d.generatedAtUtc,
  ).length;
  const anyGenerated = pkg.documents.some((d) => d.generatedAtUtc);

  return (
    <section className="grid gap-space-4">
      <header className="flex flex-wrap items-center justify-between gap-space-3">
        <h2 className="text-h3">Submission package</h2>
        {pkg.sentAtUtc ? (
          <StatusBadge tone="ok">
            Sent <Timestamp value={pkg.sentAtUtc} />
          </StatusBadge>
        ) : anyGenerated ? (
          <StatusBadge tone="progress">
            {generatedCount} of {authoredCount} written, not yet sent
          </StatusBadge>
        ) : null}
      </header>

      {/*
        🔴 The LEGACY package, when this submission has one. It is the frozen
        record of what actually went out, and the documents beside it are
        NOT it carved up — nothing splits it. Shown above the cards so a
        specialist reading a carried-over document can see what the sent version
        was.
      */}
      {/*
        Q18 — the buyer's identity (type, company, title, equity source,
        operator) changed after a document was written, so its stored prose may
        describe the buyer as they were. A NOTICE, never a gate: the sends stay
        enabled, and the fixed copy around the prose already re-renders live.
      */}
      {pkg.buyerIdentityChangedSinceGeneration ? (
        <p className="text-small text-status-warning m-0" data-testid="buyer-identity-changed">
          {BUYER_IDENTITY_CHANGED_NOTICE}
        </p>
      ) : null}

      <BuyerCommunicationsSwitch
        submissionId={submissionId}
        buyerLeadId={buyerLeadId}
        pkg={pkg}
        canEdit={canEdit}
      />
      <ReferralDisclosureControl submissionId={submissionId} pkg={pkg} canEdit={canEdit} />

      {pkg.legacyPackageDocumentId ? (
        <p className="text-small text-gray-mid m-0" data-testid="legacy-package">
          A single-file package was generated
          {pkg.legacyPackageGeneratedAtUtc ? (
            <>
              {" "}
              <Timestamp value={pkg.legacyPackageGeneratedAtUtc} />
            </>
          ) : null}{" "}
          under the previous model. It is kept as the record of what was sent —
          the documents below replace it going forward and are not made from it.
        </p>
      ) : null}

      <div className="grid gap-space-3" data-testid="package-documents">
        {pkg.documents.map((doc) => (
          <DocumentCard
            key={doc.kind}
            submissionId={submissionId}
            buyerLeadId={buyerLeadId}
            letterOverrideSubmissionId={prequalificationLetterOverrideSubmissionId}
            doc={doc}
            canEdit={canEdit}
            onEdit={() => setEditing(doc)}
          />
        ))}
      </div>

      {pkg.documents.some((d) => d.wordAvailable) ? (
        <p className="text-small text-gray-mid m-0" data-testid="word-copy-helper">
          {WORD_COPY_HELPER}
        </p>
      ) : null}

      {canEdit ? (
        <div className="flex flex-wrap items-center gap-space-3">
          {/*
            🔴 Generate all is a CONVENIENCE over the same per-document path,
            not a cheaper one. It composes the cover letter LAST so it reads the
            others rather than predicting them, which is the whole reason
            it exists as a server call rather than a click per card in document
            order.
          */}
          <Button
            onClick={() => generateAll.mutate()}
            disabled={writingDocument}
          >
            {generateAll.isPending
              ? "Generating…"
              : anyGenerated
                ? "Regenerate all"
                : "Generate all"}
          </Button>
          <Button
            variant="secondary"
            onClick={() => setSending(true)}
            disabled={!anyGenerated}
          >
            Send to listing broker
          </Button>
          {/*
            🔴 The two sends are independent and either may go first, but
            reaching the listing broker is what moves the submission forward and
            earns the specialist an allocation.

            Disabled rather than hidden when there is no address: the action
            exists, it is the buyer record that is incomplete, and the reason
            below says which. Hiding it would leave a specialist looking for a
            button a colleague can see.
          */}
          <Button
            variant="secondary"
            onClick={() => setSendingToBuyer(true)}
            disabled={!pkg.canSendToBuyer || !anyGenerated}
          >
            Send to buyer
          </Button>
        </div>
      ) : (
        <p className="text-small text-gray-mid m-0">
          Only this submission&apos;s consultant can write or send these
          documents.
        </p>
      )}

      {/* The blocked reason, rendered where the blocked button is rather than in
          a tooltip nobody opens. Server-sourced — never composed here. */}
      {canEdit && anyGenerated && !pkg.canSendToBuyer && pkg.sendToBuyerBlockedReason ? (
        <p className="text-small text-gray-mid m-0" data-testid="send-to-buyer-blocked">
          {pkg.sendToBuyerBlockedReason}
          {buyerLeadId ? (
            <>
              {" "}
              <a href={`/buyers/${buyerLeadId}`} className="text-gold-shadow hover:underline">
                Open the buyer record
              </a>
            </>
          ) : null}
        </p>
      ) : null}

      {editing ? (
        <DocumentNarrativeEditor
          submissionId={submissionId}
          doc={editing}
          onClose={() => setEditing(null)}
        />
      ) : null}

      {sending ? (
        <SendPackageModal
          submissionId={submissionId}
          onClose={() => setSending(false)}
          onMarkSentExternally={() => {
            setSending(false);
            markSent.mutate();
          }}
        />
      ) : null}

      {sendingToBuyer ? (
        <SendPackageToBuyerModal
          submissionId={submissionId}
          onClose={() => setSendingToBuyer(false)}
          onMarkSentExternally={() => {
            setSendingToBuyer(false);
            markSentToBuyer.mutate();
          }}
        />
      ) : null}
    </section>
  );
}

/** Q18 — the non-blocking notice when the buyer's identity moved after
 *  generation. Exported so the test pins the exact owner-approved wording. */
export const BUYER_IDENTITY_CHANGED_NOTICE = "Buyer details changed since generation — regenerate";

/** Under the cards whenever any document offers a Word copy — owner-approved
 *  wording, pinned verbatim by the test. */
export const WORD_COPY_HELPER =
  "Edited copies are sent outside Phoenix and aren't recorded here — log the send with Log contact.";

export const BUYER_COMMS_LABEL = "Buyer handles broker communications";

export const BUYER_COMMS_HELP =
  "When this is on, the cover letter and the Next Steps document print the buyer's phone and "
  + "email and invite the listing broker to contact the buyer directly. You stay available to "
  + "help. When it is off, the broker is asked to reply to you for the buyer's contact details. "
  + "The Potential Deal Structure never prints the buyer's contact details.";

export const BUYER_COMMS_CONTACT_MISSING =
  "This is on, but the buyer has no phone or email on file, so both sends are blocked. Add one "
  + "to the buyer record, or turn this off.";

/** The confirmation for one direction of the switch. Every confirmation says
 *  what changes and that files already out keep their wording; a package
 *  already sent to the broker also needs sending again. */
export function buyerCommsConfirmMessage(enabling: boolean, alreadySentToBroker: boolean): string {
  const change = enabling
    ? "From the next preview, download or send, the cover letter and the Next Steps document will "
      + "print the buyer's phone and email and invite the listing broker to contact the buyer "
      + "directly. The Potential Deal Structure never prints the buyer's contact details."
    : "From the next preview, download or send, the documents will go back to asking the listing "
      + "broker to reply to you for the buyer's contact details.";
  const kept = " PDFs already downloaded or sent keep the wording they had.";
  const resend = alreadySentToBroker
    ? " This package has already been sent to the listing broker, so send it again if the "
      + "broker should have the version that matches."
    : "";
  return change + kept + resend;
}

/**
 * D8 — "Buyer handles broker communications", the per-submission switch.
 *
 * <p>🔴 <b>Every gate is the server's.</b> Disabled-with-reason comes from
 * `buyerHandlesBrokerCommunicationsBlockedReason` (no phone or email on the
 * record) — the same predicate the PUT enforces and both sends re-check — and
 * the error line from `directContactMissing`. Nothing here decides whether the
 * buyer has a contact detail.</p>
 *
 * <p>Turning it either way asks first, because the change reaches documents
 * the specialist may already have downloaded or sent, and those keep their
 * wording.</p>
 */
function BuyerCommunicationsSwitch({
  submissionId,
  buyerLeadId,
  pkg,
  canEdit,
}: {
  submissionId: string;
  buyerLeadId?: string;
  pkg: BuyerSubmissionPackageDto;
  canEdit: boolean;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [confirming, setConfirming] = useState<boolean | null>(null);

  const toggle = useMutation({
    mutationFn: (enabled: boolean) =>
      buyerSubmissionsApi.setBuyerHandlesBrokerCommunications(submissionId, enabled),
    onSuccess: (_pkg, enabled) => {
      // The package read carries the switch and its reasons; the prefix also
      // covers the send drafts, whose attachments re-render under it.
      void qc.invalidateQueries({ queryKey: buyerSubmissionKeys.all });
      toast.success(
        enabled
          ? "The buyer now handles broker communications."
          : "Broker communications come back through you.");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const on = pkg.buyerHandlesBrokerCommunications;
  const blockedReason = !on ? pkg.buyerHandlesBrokerCommunicationsBlockedReason : null;

  return (
    <div className="grid gap-space-1" data-testid="buyer-comms-switch">
      <label className="flex items-center gap-space-2 text-small font-medium">
        <input
          type="checkbox"
          role="switch"
          checked={on}
          aria-checked={on}
          disabled={!canEdit || toggle.isPending || Boolean(blockedReason)}
          onChange={() => setConfirming(!on)}
        />
        {BUYER_COMMS_LABEL}
      </label>
      <p className="text-caption text-gray-mid m-0">{BUYER_COMMS_HELP}</p>
      {blockedReason ? (
        <p className="text-caption text-gray-mid m-0" data-testid="buyer-comms-blocked">
          {blockedReason}
          {buyerLeadId ? (
            <>
              {" "}
              <a href={`/buyers/${buyerLeadId}`} className="text-gold-shadow hover:underline">
                Open the buyer record
              </a>
            </>
          ) : null}
        </p>
      ) : null}
      {pkg.directContactMissing ? (
        <p role="alert" className="text-caption text-status-error m-0" data-testid="buyer-comms-missing">
          {BUYER_COMMS_CONTACT_MISSING}
        </p>
      ) : null}

      <ConfirmModal
        open={confirming !== null}
        title={confirming ? `Turn on “${BUYER_COMMS_LABEL}”?` : `Turn off “${BUYER_COMMS_LABEL}”?`}
        message={buyerCommsConfirmMessage(confirming ?? false, Boolean(pkg.sentAtUtc))}
        confirmLabel={confirming ? "Turn on" : "Turn off"}
        onConfirm={() => {
          const enabled = confirming ?? false;
          setConfirming(null);
          toggle.mutate(enabled);
        }}
        onCancel={() => setConfirming(null)}
      />
    </div>
  );
}

/**
 * One document's card.
 *
 * <p>🔴 <b>THREE shapes now, and `isAuthored` no longer tells them apart.</b>
 * It used to: there was exactly one non-authored document and its card was
 * "the letter's card". There are two, and they are opposites. The
 * prequalification letter is per BUYER — pulled from the funnel, and WHICH
 * letter is a choice this card owns. The seller brochure is per ORGANISATION —
 * one uploaded PDF resolved live, the same file on every submission, with
 * nothing here to choose and nothing here to pull. Both offer Generate and Edit
 * to nobody.</p>
 *
 * <p>🔴 <b>So this card branches on the KIND, in ONE place
 * ({@link isBrochureKind}), and that reverses a rule this comment used to
 * state.</b> The rule was right while `isAuthored` was a complete
 * discriminator and is wrong now — gating the brochure's affordances on the
 * fields it happens to carry would be worse, not better: a brochure the server
 * could not resolve arrives with those fields null and would silently render as
 * a letter, offering "Pull the current letter" for a document no pull can
 * reach.</p>
 *
 * <p>🔴 <b>NOTHING IN THIS SPA IS EXHAUSTIVE OVER THE KIND UNION</b> — no
 * `Record<BuyerSubmissionDocumentKind, …>` exists, so a new member compiles
 * clean and is only ever wrong on screen. The complete list of places a kind is
 * looked at, as of the brochure:</p>
 *
 * <ol>
 *   <li>this card's `isBrochureKind`, the only equality test on a kind
 *       anywhere;</li>
 *   <li>`doc.kind` as the card's React key and `data-testid`, and as the path
 *       segment on generate / narrative / PDF — opaque, correct for any
 *       member;</li>
 *   <li>the send picker's exclusion set and `includeKinds`, keyed on
 *       `a.kind` from the SERVER's manifest — opaque, and seeded from the
 *       server's own `defaultSelectedKinds`, so which documents a send starts
 *       with is the server's answer and never a list here;</li>
 *   <li>`SendPreviewModal`, which types its row identity as a bare `string` and
 *       never learns a kind at all.</li>
 * </ol>
 *
 * <p>Every one of (2)–(4) is opaque by construction. (1) is the seam a new kind
 * has to be read against.</p>
 *
 * <p>🔴 <b>WHICH letter is a property of that card, not a section beside it.</b>
 * The selector used to be its own "Package letter" section below the whole
 * panel, where it read as a setting about the submission — so the one card that
 * shows what the letter IS and the one control that decides WHICH letter it is
 * were separated by four other documents. They answer the same question and now
 * sit in the same box, which is also where a specialist looking at a wrong
 * letter goes to fix it.</p>
 */
function DocumentCard({
  submissionId,
  buyerLeadId,
  letterOverrideSubmissionId,
  doc,
  canEdit,
  onEdit,
}: {
  submissionId: string;
  buyerLeadId?: string;
  letterOverrideSubmissionId?: string | null;
  doc: BuyerSubmissionDocumentDto;
  canEdit: boolean;
  onEdit: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: buyerSubmissionKeys.all });
  };

  const writingDocument = useDocumentWriteInFlight(submissionId);

  const generate = useMutation({
    mutationKey: buyerSubmissionKeys.packageDocumentWrite(submissionId),
    mutationFn: () => buyerSubmissionsApi.generateDocument(submissionId, doc.kind),
    onSuccess: () => {
      invalidate();
      toast.success(`${doc.title} generated.`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const refreshLetter = useMutation({
    mutationFn: () => buyerSubmissionsApi.refreshPackageLetter(submissionId),
    onSuccess: () => {
      invalidate();
      toast.success("Pulled the current prequalification letter.");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const present = doc.isAuthored
    ? Boolean(doc.generatedAtUtc)
    : doc.letterState === "present" || doc.letterState === "stale";

  const brochure = isBrochureKind(doc.kind);
  const sourceSummary = sourceFileSummary(doc);

  /*
    🔴 CURRENT STATE, NEVER A SEND — and this card is the one that needed
    saying it.

    An already-sent submission renders every card, this one included, and the
    brochure it shows is whatever is on file TODAY: it is resolved live from a
    single organisation-wide PDF that an admin can replace, so it is not
    evidence of what any historical send carried. "Included" or "Sent" here
    would be a claim about the past made out of a read of the present, on the
    one card whose subject can change under a submission that is finished.

    "Not written yet" is wrong for a different reason: nobody writes it.
  */
  const badge = brochure
    ? present
      ? { tone: "ok" as const, label: "Live brochure on file" }
      : { tone: "neutral" as const, label: "No brochure uploaded" }
    : present
      ? { tone: "ok" as const, label: "Ready" }
      : { tone: "neutral" as const, label: "Not written yet" };

  return (
    <article
      className="border border-gray-mid/30 rounded p-space-4 grid gap-space-2"
      data-testid={`package-document-${doc.kind}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-space-3">
        <div className="grid gap-space-1">
          <h3 className="text-body font-medium m-0">{doc.title}</h3>
          <p className="text-small text-gray-mid m-0">{doc.subtitle}</p>
        </div>
        <StatusBadge tone={badge.tone}>{badge.label}</StatusBadge>
      </div>

      {/*
        🔴 THE PROVENANCE LINE. A carried-over document holds one section of the
        retired ten-section package while a generated one is several times
        longer, and the two are otherwise indistinguishable on screen — which
        makes the specialist who cannot tell them apart the one who sends the
        thin one.
      */}
      {doc.carriedOver ? (
        <p className="text-small text-status-warning m-0" data-testid="carried-over">
          {CARRIED_OVER_NOTE}
        </p>
      ) : null}

      {/* Surfaced, never enforced: nothing blocks and nothing auto-regenerates.
          Only ever true for the cover letter, which is the only document that
          describes the others. */}
      {doc.describesStaleSiblings ? (
        <p className="text-small text-gray-mid m-0" data-testid="describes-stale-siblings">
          Another document has changed since this letter was written, so it may
          describe the package as it used to be.
        </p>
      ) : null}

      {/*
        🔴 A SKIPPED DOCUMENT, not a failed one. `generateAll` writes every
        other authored document and passes over this one, so without a sentence
        here the specialist sees the rest appear and this one quietly not — the
        most confusing outcome available. The reason comes from the server, from the
        SAME expression the generator consults, so the card and the generator
        cannot come to disagree about whether this document is writable.

        No link. The way back is the wizard on the Deal structure tab, and this panel
        renders inside the record drawer as well as on the page, where the URL's
        `tab` key belongs to the record underneath — a link here would navigate
        the wrong surface. The sentence names the door instead.
      */}
      {doc.blockedReason ? (
        <p className="text-small text-status-warning m-0" data-testid="document-blocked">
          {doc.blockedReason}
        </p>
      ) : null}

      {/* Q1 — a document 3 in an older format. The sentence is the server's
          (it names which older format and the way forward); the constant is
          only for a server that does not send one. An edit is refused
          server-side, so none is offered. */}
      {doc.olderFormat ? (
        <p className="text-small text-status-warning m-0" data-testid="document-older-format">
          {doc.olderFormatNote?.trim() ? doc.olderFormatNote : OLDER_FORMAT_NOTE}
        </p>
      ) : null}

      {/* Q35 — something the specialist entered was left off for its wording.
          The sentence is the server's. */}
      {doc.omissionNotice ? (
        <p className="text-small text-status-warning m-0" data-testid="document-omission">
          {doc.omissionNotice}
        </p>
      ) : null}

      {/* The letter's own state sentence, composed server-side so the panel
          never assembles a reason from parts. */}
      {doc.letterMessage ? (
        <p className="text-small text-gray-mid m-0" data-testid="letter-message">
          {doc.letterMessage}
          {/*
            🔴 The remedy for an absent LETTER is the buyer's record — it is
            qualified per buyer, and that is where a specialist goes. The remedy
            for an absent BROCHURE is an admin uploading one for the whole
            organisation, which no buyer record can do: the link would send a
            specialist to a page that cannot fix what they just read.
          */}
          {!brochure && doc.letterState === "absent" && buyerLeadId ? (
            <>
              {" "}
              <a href={`/buyers/${buyerLeadId}`} className="text-gold-shadow hover:underline">
                Open the buyer record
              </a>
            </>
          ) : null}
        </p>
      ) : null}

      {/* Only the pulled LETTER has a choice to make: the authored documents are
          written for this package, and the brochure is one organisation-wide
          PDF — there is no other version of either to choose between. */}
      {!doc.isAuthored && !brochure && buyerLeadId ? (
        <div className="grid gap-space-2" data-testid="package-letter-choice">
          <span className="text-caption text-gray-mid uppercase tracking-wider">
            Which letter this package encloses
          </span>
          <PackageLetterOverrideField
            buyerLeadId={buyerLeadId}
            submissionId={submissionId}
            value={letterOverrideSubmissionId}
            canEdit={canEdit}
          />
        </div>
      ) : null}

      {/*
        The brochure's own file. Rendered from the FIELDS rather than from
        "this card is the brochure": they are populated for this kind alone, so
        a card that gained them later would say so without an edit here, and a
        brochure whose file the server could not resolve says nothing rather
        than "0 KB".
      */}
      {sourceSummary ? (
        <p className="text-small text-gray-mid m-0" data-testid="source-file-meta">
          {sourceSummary}
        </p>
      ) : null}

      {doc.generatedAtUtc ? (
        <p className="text-small text-gray-mid m-0">
          Written <Timestamp value={doc.generatedAtUtc} />
          {doc.contentHash ? ` · ${doc.contentHash.slice(0, 12)}` : null}
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-space-3">
        {/* 🔴 Preview and Download are both READS and stay available to
            everyone — a specialist who cannot send still has every reason to
            look at what was sent, and to take a copy of it.

            "View" on the brochure, and it is the SAME action on the SAME
            per-document endpoint — only the word changes. You preview a thing
            you are about to produce; the brochure is a finished PDF somebody
            else uploaded, and the specialist opening it is checking which one
            is live rather than reviewing their own draft. A second button would
            have been two controls issuing one identical fetch. */}
        {present ? (
          <>
            <Button
              variant="secondary"
              onClick={() => void openDocument(submissionId, doc.kind, toast.error)}
            >
              {brochure ? "View" : "Preview"}
            </Button>
            <Button
              variant="secondary"
              onClick={() =>
                void saveDocument(submissionId, doc.kind, cardFileName(doc), toast.error)
              }
            >
              Download
            </Button>
          </>
        ) : null}

        {/* An editable Word copy, only where the server says one exists. A
            staff download: no send or send picker ever lists it. */}
        {doc.wordAvailable ? (
          <Button
            variant="secondary"
            onClick={() =>
              void saveDocument(
                submissionId,
                doc.kind,
                `${doc.title}.docx`,
                toast.error,
                "docx",
              )
            }
          >
            Download Word
          </Button>
        ) : null}

        {canEdit && doc.isAuthored && !doc.blockedReason ? (
          <>
            <Button
              variant={doc.generatedAtUtc ? "text" : "primary"}
              onClick={() => generate.mutate()}
              disabled={writingDocument}
            >
              {generate.isPending
                ? "Generating…"
                : doc.generatedAtUtc
                  ? "Regenerate"
                  : "Generate"}
            </Button>
            {/*
              🔴 NON-EMPTY, not truthy. `{}` is truthy in JavaScript, and the
              Next Steps document now returns exactly that — it is fixed copy
              with no narrative blocks at all, so `CurrentNarrative` hands back
              an empty dictionary rather than null. Gating on the object alone
              rendered Edit on that card and opened a modal with no fields and a
              permanently disabled Save.
            */}
            {Object.keys(doc.narrative ?? {}).length > 0 && !doc.olderFormat ? (
              <Button variant="secondary" onClick={onEdit}>
                Edit
              </Button>
            ) : null}
          </>
        ) : null}

        {/*
          🔴 THE LETTER'S PULL, and the brochure must not offer it.

          A pull pins THIS package to the letter version it reads, which is a
          real choice because a buyer can have several. The brochure is resolved
          live from one organisation-wide PDF: there is no second version to
          choose and nothing per-submission to pin, so the button would either
          do nothing or 400 — and `refreshLetter` posts to the letter endpoint,
          which knows nothing about a brochure. There is no Generate on this
          card either: `isAuthored` is false, and nothing here composes it.
        */}
        {canEdit && !doc.isAuthored && !brochure ? (
          <Button
            variant={present ? "text" : "primary"}
            onClick={() => refreshLetter.mutate()}
            disabled={refreshLetter.isPending}
          >
            {refreshLetter.isPending ? "Pulling…" : "Pull the current letter"}
          </Button>
        ) : null}
      </div>
    </article>
  );
}

/**
 * The ONE kind equality test in the SPA.
 *
 * <p>Named and used from a single place so that "which documents are the
 * brochure" has one answer. A second `=== "SellerBrochure"` somewhere else is a
 * second answer, free to be edited without this one.</p>
 *
 * <p>⚠️ <b>NO LINK TO THE BROCHURE ADMIN, deliberately.</b> The obvious
 * affordance beside "No brochure uploaded" is a link to
 * `/deal-flow/prequalification/brochure`, where an admin replaces the file.
 * That route is gated on `Prequalification.ManageDocuments`, which a buyer
 * consultant does not hold — so an ungated link is a door that 403s, on the
 * card of the one specialist most likely to click it. Gating it needs
 * `useAuth().hasPermission` in a component that today takes its one permission
 * answer as a prop, and `renderWithProviders` mounts no `AuthProvider`, so
 * every consumer suite would need a `useAuth` mock to keep rendering. That is
 * not a plain `&lt;Link&gt;`; it is a permission surface, and it belongs to
 * whoever decides a specialist should be able to see that page at all.</p>
 */
function isBrochureKind(kind: BuyerSubmissionDocumentKind): boolean {
  return kind === "SellerBrochure";
}

/**
 * Whole KB below a megabyte, one decimal above.
 *
 * <p>⚠️ The SAME rule as `formatSize` in `SendPreviewModal`, and that is the
 * point: the brochure is listed on this card and again in the send picker one
 * scroll away, and one file rendered "706 KB" in one place and "0.7 MB" in the
 * other reads as two files. It is copied rather than imported because that one
 * is module-private in a component this change does not own, and
 * `design-system/lib/format` carries no byte formatter to hoist either into —
 * nine other files already keep their own for the same reason. Hoisting all ten
 * is its own change; making this one disagree is not.</p>
 */
function formatFileSize(bytes: number): string {
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.max(1, Math.round(kb))} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

/**
 * The brochure's file, as one line — "… Brochure 2.15.pdf · 706 KB · 15 pages".
 *
 * <p>Null when there is no file name, which is exactly the state a brochure
 * nobody has uploaded is in: the card's own state sentence says so, and a
 * metadata line beside it would be a second and worse answer to the same
 * question.</p>
 *
 * <p>🔴 The size and the page count drop INDEPENDENTLY. A page count is the
 * one of the three a server may legitimately fail to read off a PDF that is
 * otherwise fine, and an all-or-nothing line would cost the reader the file
 * name and the size to report that. Both are guarded on `&gt; 0` rather than on
 * presence, because a zero-byte or zero-page file is a broken read, not a
 * fact worth printing.</p>
 */
function sourceFileSummary(doc: BuyerSubmissionDocumentDto): string | null {
  if (!doc.sourceFileName) return null;

  const parts = [doc.sourceFileName];
  if (typeof doc.sourceSizeBytes === "number" && doc.sourceSizeBytes > 0) {
    parts.push(formatFileSize(doc.sourceSizeBytes));
  }
  if (typeof doc.sourcePageCount === "number" && doc.sourcePageCount > 0) {
    parts.push(`${doc.sourcePageCount} ${doc.sourcePageCount === 1 ? "page" : "pages"}`);
  }
  return parts.join(" · ");
}

/**
 * The titles of the documents a generate-all passed over, as a readable clause.
 *
 * <p>Only ever called with a non-empty list, and today only ever with one
 * member — one document can be blocked. It reads a list anyway because the
 * shape that produces it is a filter, and a filter that grows a second member
 * would otherwise render "the A, B" with no conjunction and nobody watching.</p>
 */
function listTitles(docs: BuyerSubmissionDocumentDto[]): string {
  const titles = docs.map((d) => d.title);
  if (titles.length === 1) return titles[0];
  return `${titles.slice(0, -1).join(", ")} and ${titles[titles.length - 1]}`;
}

/**
 * The name a card's Download gives the file.
 *
 * <p>⚠️ <b>This is NOT the name a listing broker receives.</b> That one is
 * `BuyerPackageAttachmentDto.fileName`, composed server-side, and it reaches
 * the client only on a SEND draft — which the card does not read, because
 * fetching a whole manifest to label one button would be a second read of the
 * same facts. So a card names its download from the document's own title: a
 * local copy, distinguishable from its siblings, which is what the button
 * is for. The send picker's per-row Download and the multi-file Download both
 * use the server's name, and those are the copies that travel.</p>
 */
function cardFileName(doc: BuyerSubmissionDocumentDto): string {
  return `${doc.title}.pdf`;
}

/**
 * The specialist's edit (L30). Partial by design — only changed blocks are
 * sent, and the server keeps the rest.
 *
 * <p>🔴 <b>Renders exactly the keys the DOCUMENT came back with</b>, never a
 * fixed list. The server narrows each document's narrative to the blocks it
 * owns, so iterating a hard-coded set here would show a Buyer Profile editor
 * the cover letter's prose — and a specialist editing it would be editing text
 * that document never renders.</p>
 */
function DocumentNarrativeEditor({
  submissionId,
  doc,
  onClose,
}: {
  submissionId: string;
  doc: BuyerSubmissionDocumentDto;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const initial = doc.narrative ?? {};
  const [draft, setDraft] = useState<Record<string, string>>(initial);

  const keys = Object.keys(initial);

  const writingDocument = useDocumentWriteInFlight(submissionId);

  const save = useMutation({
    mutationKey: buyerSubmissionKeys.packageDocumentWrite(submissionId),
    mutationFn: () => {
      const changed: Record<string, string | null> = {};
      for (const key of keys) {
        if (draft[key] !== initial[key]) changed[key] = draft[key];
      }
      return buyerSubmissionsApi.updateDocumentNarrative(submissionId, doc.kind, {
        narrative: changed,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: buyerSubmissionKeys.all });
      toast.success(`${doc.title} updated.`);
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const dirty = keys.some((key) => draft[key] !== initial[key]);

  return (
    <Modal
      open
      onClose={onClose}
      title={`Edit the ${doc.title.toLowerCase()}`}
      size="lg"
      footer={
        <div className="flex flex-wrap items-center justify-end gap-space-3">
          {writingDocument && !save.isPending ? (
            <span className="text-small text-gray-mid">{DOCUMENT_WRITE_IN_FLIGHT}</span>
          ) : null}
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => save.mutate()} disabled={!dirty || writingDocument}>
            {save.isPending ? "Saving…" : "Save and re-render"}
          </Button>
        </div>
      }
    >
      <p className="text-small text-gray-mid">
        Every figure in this document is calculated and rendered by Phoenix —
        these are the words around them. Saving re-renders the PDF, and touches
        no other document.
      </p>
      <div className="grid gap-space-4 mt-space-4">
        {keys.map((key) => (
          <label key={key} className="grid gap-space-2">
            <span className="text-small font-medium">{blockLabel(key)}</span>
            <Textarea
              rows={8}
              value={draft[key] ?? ""}
              onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
            />
          </label>
        ))}
      </div>
    </Modal>
  );
}

/**
 * True while any document write for this submission is in flight — Generate all,
 * a card's Generate, or the editor's save, which share one mutation key — so
 * every one of them waits for the others. The server refuses a document whose
 * own row, or (for the cover letter) whose siblings' prose, changed while it was
 * written; this keeps the panel from causing that itself. Counted across every
 * mounted panel, so a drawer and the page agree.
 */
function useDocumentWriteInFlight(submissionId: string): boolean {
  return useIsMutating({ mutationKey: buyerSubmissionKeys.packageDocumentWrite(submissionId) }) > 0;
}

/** Why the editor's save is waiting. */
export const DOCUMENT_WRITE_IN_FLIGHT =
  "Another document in this package is being written. You can save when it finishes.";

/** The provenance sentence, in ONE place. The card and the send picker say it
 *  about the same documents, and two copies is two sentences the moment either
 *  is reworded. */
const CARRIED_OVER_NOTE =
  "Carried over from the previous package — regenerate for the full version.";

/** Q1 — the card flag on an older-format document 3, used only when the server
 *  sends no `olderFormatNote` (an older server). */
const OLDER_FORMAT_NOTE =
  "Older format (Preliminary Offer Structure) — regenerate to update it."; // retired-term-ok: frozen legacy doc 3 (N4)

/** Q1 — the send's second action (Appendix 4). The message is the server's. */
const OLDER_FORMAT_CONFIRM_TITLE = "Older format document";
const OLDER_FORMAT_CONFIRM_LABEL = "Send anyway";
const OLDER_FORMAT_CANCEL_LABEL = "Go back";

/** Why a document nobody has written cannot be ticked. */
// 🔴 Deliberately says WHAT, not HOW. Two of the seven documents are pulled
// rather than written — the prequalification letter and the seller brochure —
// and "generate it" is false for both: nobody writes them here, and the
// brochure's own card three inches away says exactly that. This row sits in a
// send confirmation, which is not where a document gets made anyway, so the
// sentence a specialist needs is that it will not be attached.
const NOT_AVAILABLE = "Not available yet — it will not be attached.";

/** Why a send with an empty picker cannot go. */
const NOTHING_SELECTED = "Nothing is selected. Tick at least one document to send.";

/** Whole megabytes, the way the server-side ceiling is stated. */
const mb = (n: number) => Math.round(n / (1024 * 1024));

/**
 * Why this SELECTION cannot be sent, or null.
 *
 * <p>🔴 The one reason composed on the client, and it has to be: the ceiling is
 * a question about the ticked set, and there is nothing to ask the server about
 * until a specialist has ticked. The limit itself is still the server's — read
 * off the manifest — so the two cannot disagree about where the line is, only
 * about which files are being weighed, which is the whole point of a picker.</p>
 */
function selectionBlockedReason(
  selected: readonly BuyerPackageAttachmentDto[],
  limitBytes: number,
): string | null {
  if (selected.length === 0) return NOTHING_SELECTED;

  const total = selected.reduce((sum, a) => sum + a.sizeBytes, 0);
  if (total <= limitBytes) return null;

  return (
    `The ${selected.length} selected documents come to ${mb(total)} MB, over the `
    + `${mb(limitBytes)} MB email limit. Untick a document, or download them and `
    + `send from your own email.`
  );
}

/**
 * The per-send picker, over the SERVER's resolved manifest and nothing else.
 *
 * <p>🔴 <b>State tracks what is EXCLUDED, never what is selected.</b> The modal
 * opens before its draft resolves, so a selected-set initialised from the
 * manifest would be initialised from `undefined` and would need an effect to
 * catch up — which is the props-into-state class that leaves a control showing
 * a figure the query has already moved past. An exclusion set is empty by
 * default and "empty means everything generated" is exactly the wanted default,
 * at every point in the query's life.</p>
 *
 * <p>Tick state lives on the modal component, which unmounts when the modal
 * closes — so it is per-send by construction, and the broker's picker and the
 * buyer's are independent because each modal holds its own.</p>
 */
function useAttachmentSelection(
  submissionId: string,
  manifest: BuyerPackageAttachmentManifestDto | undefined,
  onError: (message: string) => void,
) {
  /**
   * 🔴 SEEDED FROM THE SERVER, once the manifest lands.
   *
   * The exclusion set used to start empty, which meant "everything ticked" and
   * was the whole of the rule. It is now the server's `defaultSelectedKinds`
   * inverted — so a listing broker does not start out sending the buyer's own
   * copy of the package, and the same predicate the send falls back to decides
   * what the specialist sees ticked.
   *
   * Keyed on the manifest's identity rather than assigned in an effect: an
   * effect would fight a specialist who unticked a row while a background
   * refetch was in flight, silently re-ticking it under their cursor. This
   * re-seeds only when a genuinely different manifest arrives, and a manifest
   * with no default (an older fixture) keeps the previous behaviour.
   */
  const [excluded, setExcluded] = useState<ReadonlySet<string>>(() => new Set());
  const [seededFrom, setSeededFrom] = useState<
    BuyerPackageAttachmentManifestDto | undefined
  >(undefined);

  if (manifest !== undefined && manifest !== seededFrom) {
    setSeededFrom(manifest);
    const defaults = manifest.defaultSelectedKinds;
    setExcluded(
      defaults === undefined
        ? new Set()
        : new Set(
            manifest.attachments
              .map((a) => a.kind)
              .filter((kind) => !defaults.includes(kind)),
          ),
    );
  }

  const attachments = manifest?.attachments ?? [];
  const selected = attachments.filter((a) => !excluded.has(a.kind));

  const toggle = (kind: string) =>
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(kind)) next.delete(kind);
      else next.add(kind);
      return next;
    });

  const rows: SendPreviewSelectableAttachment[] | undefined = manifest
    ? [
        ...attachments.map((a) => ({
          key: a.kind,
          name: a.fileName,
          sizeBytes: a.sizeBytes,
          carriedOver: a.carriedOver,
          selected: !excluded.has(a.kind),
          onToggle: () => toggle(a.kind),
          onDownload: () => void saveDocument(submissionId, a.kind, a.fileName, onError),
        })),
        // 🔴 Listed, untickable. A document nobody has written is the most
        // useful row here — omitting it makes an incomplete package look
        // complete, and nothing else on the confirmation says it exists.
        ...manifest.missingTitles.map((title) => ({
          key: `missing:${title}`,
          name: title,
          selected: false,
          disabled: true,
          disabledReason: NOT_AVAILABLE,
        })),
      ]
    : undefined;

  return {
    rows,
    /** What the send posts. Non-empty by construction — an empty pick blocks. */
    includeKinds: selected.map((a) => a.kind),
    blockedReason: manifest
      ? selectionBlockedReason(selected, manifest.attachmentLimitBytes)
      : null,
  };
}

/**
 * What is NOT going out, named rather than silently omitted. Returns null when
 * everything is there, so a caller can render nothing.
 *
 * <p>The picker lists these rows too, with the same reason in each. The
 * duplication is deliberate: this is the one-line summary above the fold, and a
 * specialist who never scrolls to the picker still learns the package is
 * incomplete.</p>
 */
function MissingNote({ manifest }: { manifest?: BuyerPackageAttachmentManifestDto }) {
  if (!manifest || manifest.missingTitles.length === 0) return null;
  return (
    <p className="text-small text-gray-mid m-0" data-testid="send-missing-documents">
      Not available, and not attached: {manifest.missingTitles.join(", ")}.
    </p>
  );
}

/**
 * What a specialist can do to the message itself.
 *
 * <p>🔴 <b>Reset is the affordance that makes editing safe.</b> Every field on
 * this confirmation is an override laid over a server-composed draft, and until
 * now the only way out of an edit was to close the modal and reopen it —
 * which also threw away the tick state, and which nothing on screen suggested.
 * Clearing the overrides is the whole of it: the fields fall straight back
 * through to `draft.data`, so "the generated version" is whatever the server
 * currently says rather than a snapshot taken when the modal opened.</p>
 *
 * <p>Copy takes the SHOWN text, overrides included — a specialist copying into
 * their own client wants what they are looking at, which is the same reason
 * Download hands over the real files rather than regenerating them.</p>
 *
 * <p>🔴 <b>AND IT TAKES THE ROLE STATEMENT, WHICH IS NOT A DETAIL.</b> Phoenix's
 * regulated role statement is no longer part of the composed body: the transport
 * decorator re-appends it, so a Phoenix send carries it exactly once. THE MANUAL
 * PATH HAS NO DECORATOR. A specialist copies the body, pastes it into their own
 * client and sends it themselves — so a copy of the body alone is a message that
 * reaches a counterparty with the statement silently missing, on the one path
 * nothing downstream inspects. Copying it is what keeps the two paths saying the
 * same thing, and it is why the trailer above is rendered rather than assumed:
 * a specialist who cannot see it has no way to know it is part of what they are
 * about to paste.</p>
 *
 * <p>The separator is a blank line, the same one the decorator uses. The
 * sentence itself is NEVER retyped here and never imported — it comes off the
 * draft, so the copy carries whatever the server actually appends.</p>
 */
function MessageActions({
  dirty,
  onReset,
  subject,
  body,
  roleStatement,
}: {
  dirty: boolean;
  onReset: () => void;
  subject: string;
  body: string;
  roleStatement?: string | null;
}) {
  const toast = useToast();

  return (
    <>
      <Button
        variant="text"
        size="sm"
        disabled={!dirty}
        onClick={onReset}
        data-testid="send-reset-draft"
      >
        Reset to the generated version
      </Button>
      <Button
        variant="text"
        size="sm"
        disabled={!body}
        onClick={() => {
          // The education email's pattern — including the toast, so the module
          // has one word for "it is on your clipboard" — plus the trailer the
          // transport would have added on a Phoenix send.
          void navigator.clipboard?.writeText(
            roleStatement
              ? `${subject}\n\n${body}\n\n${roleStatement}`
              : `${subject}\n\n${body}`,
          );
          toast.success("Copied.");
        }}
        data-testid="send-copy-body"
      >
        Copy email body
      </Button>
    </>
  );
}

/**
 * Both paths (L31): Phoenix sends it with every generated document attached, or
 * the specialist downloads them and the body text and sends from their own
 * client. Either way the submission is stamped.
 *
 * <p>Renders through the SHARED send preview, so this send, the listing intro
 * email and the seller prequalification invitation all state their recipient,
 * their sender identity and their attachments the same way.</p>
 *
 * <p>🔴 <b>A1 — the no-letter confirmation.</b> When no Phoenix
 * prequalification letter will be attached, Send requires a second, explicit
 * action naming what is missing. It is a real dialog, not a pre-ticked box and
 * not a banner: the rule it enforces is that Phoenix does not present a buyer it
 * has not qualified, and a control somebody can scroll past does not enforce
 * it.</p>
 */
function SendPackageModal({
  submissionId,
  onClose,
  onMarkSentExternally,
}: {
  submissionId: string;
  onClose: () => void;
  onMarkSentExternally: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const identity = useSenderIdentity();
  const [overrides, setOverrides] = useState<{ subject?: string; body?: string }>({});
  const [confirmingNoLetter, setConfirmingNoLetter] = useState(false);

  // 🔴 ACCUMULATED, and passed to the mutation EXPLICITLY rather than read from
  // state inside it.
  //
  // Two things force this. The refusals fire in sequence — confirm the missing
  // letter, the send re-fires, and it can then be refused for an older-format
  // document — so confirming the second must not drop the first or the
  // specialist is asked about the letter again, forever. And `mutationFn` closes over the render it
  // was defined in, so `setConfirmed(...)` followed by `send.mutate()` in one
  // handler would send the STALE value: the confirmation would appear to do
  // nothing, which is the same dead end the code-branching above exists to
  // avoid.
  const [confirmed, setConfirmed] = useState({
    letter: false,
    olderFormat: false,
  });
  const [olderFormatRefusal, setOlderFormatRefusal] = useState<string | null>(null);

  const draft = useQuery({
    queryKey: buyerSubmissionKeys.packageDownload(submissionId),
    queryFn: () => buyerSubmissionsApi.getPackageDownload(submissionId),
  });

  const send = useMutation({
    mutationFn: (v: { letter: boolean; olderFormat: boolean }) =>
      buyerSubmissionsApi.sendPackage(submissionId, {
        subject: overrides.subject ?? null,
        body: overrides.body ?? null,
        confirmNoPrequalificationLetter: v.letter,
        confirmOlderFormatDocument: v.olderFormat,
        includeKinds: selection.includeKinds,
      }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: buyerSubmissionKeys.all });
      setConfirmingNoLetter(false);
      toast.success(
        `Sent to ${r.toEmail} — ${r.attachmentCount} ${
          r.attachmentCount === 1 ? "document" : "documents"
        }.`,
      );
      onClose();
    },
    onError: (e: Error) => {
      // 🔴 Branch on the CODE, never on the message. The message is copy and
      // will be reworded; a string match would stop matching the moment it was,
      // and what it would silently stop doing is opening this confirmation —
      // which turns a decision the server is asking for into a dead end.
      if (isNoLetterRefusal(e)) {
        setConfirmingNoLetter(true);
        return;
      }
      const older = olderFormatRefusalOf(e);
      if (older) {
        setOlderFormatRefusal(older);
        return;
      }
      setConfirmingNoLetter(false);
      setOlderFormatRefusal(null);
      toast.error(e.message);
    },
  });

  /** Records one confirmation and re-fires the send carrying BOTH. */
  const confirmAndSend = (which: "letter" | "olderFormat") => {
    const next = { ...confirmed, [which]: true };
    setConfirmed(next);
    setConfirmingNoLetter(false);
    setOlderFormatRefusal(null);
    send.mutate(next);
  };

  const subject = overrides.subject ?? draft.data?.subject ?? "";
  const body = overrides.body ?? draft.data?.body ?? "";
  const manifest = draft.data?.attachments;
  const selection = useAttachmentSelection(submissionId, manifest, toast.error);
  const dirty = overrides.subject !== undefined || overrides.body !== undefined;

  // 🔴 The recipient's own refusal outranks the selection's: a listing with no
  // resolvable agent cannot be sent to whatever is ticked, and telling a
  // specialist to tick a document would send them to fix the wrong thing.
  const blockedReason = draft.data?.recipientBlockedReason ?? selection.blockedReason;

  // A failed draft read used to render two blank boxes, which look exactly like
  // a package Phoenix intends to send with an empty subject and body. (It would
  // not — an unset field means the server composes its own — but nothing on
  // screen said so.)
  const replacesPreviewBody = draft.isLoading ? (
    <Skeleton className="h-48 w-full" />
  ) : draft.isError ? (
    <InlineQueryError
      what="the draft message"
      onRetry={() => void draft.refetch()}
      retrying={draft.isFetching}
    />
  ) : undefined;

  // 🔴 `notices`, NOT `replacesPreviewBody`. These two notes used to live in
  // the replacement slot, which swaps the message block out — and a fragment is
  // always truthy, so the coalesce never fell through and the subject, the body
  // and the attachment list were unreachable on this modal for the life of
  // #1136. The slot is named for what it does to its neighbours now.
  const notices = <MissingNote manifest={manifest} />;

  return (
    <>
      <SendPreviewModal
        title="Send the submission package"
        onClose={onClose}
        // 🔴 The listing agent, RESOLVED — and named. This used to say only
        // "the address on file", on the argument that naming one here would be
        // a second resolution rule free to disagree with the server's. The
        // server resolves it into the draft now, so there is one rule and this
        // is its answer: the confirmation states who it is going to, like every
        // other send in the module.
        toName={draft.data?.toName}
        toEmail={draft.data?.toEmail}
        audienceNote="the listing agent on file for this listing."
        identity={identity}
        replacesPreviewBody={replacesPreviewBody}
        notices={notices}
        subject={subject}
        onSubjectChange={(v) => setOverrides({ ...overrides, subject: v })}
        body={body}
        onBodyChange={(v) => setOverrides({ ...overrides, body: v })}
        messageActions={
          <MessageActions
            dirty={dirty}
            onReset={() => setOverrides({})}
            subject={subject}
            body={body}
            roleStatement={draft.data?.roleStatement}
          />
        }
        roleStatement={draft.data?.roleStatement}
        selectableAttachments={selection.rows}
        carriedOverNote={CARRIED_OVER_NOTE}
        footnote={
          <>
            If you would rather send from your own email, download the documents
            and then choose “I sent it myself” so the submission is still
            recorded.
          </>
        }
        secondaryAction={
          <Button
            variant="secondary"
            onClick={() => void downloadAll(submissionId, manifest, toast.error)}
          >
            Download
          </Button>
        }
        alternateAction={
          <Button variant="text" onClick={onMarkSentExternally}>
            I sent it myself
          </Button>
        }
        confirmLabel="Send from Phoenix"
        onConfirm={() => send.mutate(confirmed)}
        confirmDisabled={draft.isLoading || blockedReason !== null}
        confirmBlockedReason={blockedReason}
        pending={send.isPending}
      />

      {/*
        🔴 The second ACTION. Opened by the server's refusal rather than by a
        client-side prediction, so it appears exactly when the send would be
        refused — and the sentence is the server's, naming the buyer.
      */}
      {confirmingNoLetter ? (
        <ConfirmModal
          open
          title="No prequalification letter"
          message={
            draft.data?.noLetterConfirmationMessage ??
            "This package has no Phoenix prequalification letter."
          }
          confirmLabel="Send without the letter"
          onConfirm={() => confirmAndSend("letter")}
          onCancel={() => setConfirmingNoLetter(false)}
        />
      ) : null}

      {/* Q1 — opened by the server's refusal, branched on its CODE. */}
      {olderFormatRefusal ? (
        <ConfirmModal
          open
          title={OLDER_FORMAT_CONFIRM_TITLE}
          message={olderFormatRefusal}
          confirmLabel={OLDER_FORMAT_CONFIRM_LABEL}
          cancelLabel={OLDER_FORMAT_CANCEL_LABEL}
          onConfirm={() => confirmAndSend("olderFormat")}
          onCancel={() => setOlderFormatRefusal(null)}
        />
      ) : null}
    </>
  );
}

/**
 * The same documents, sent to the BUYER.
 *
 * <p>🔴 <b>The recipient is stated, not implied.</b> This panel hosts two sends
 * of the same documents to opposite sides of the transaction, one click apart.
 * The broker modal deliberately names no address — the server resolves the
 * listing agent, and naming one here would be a second resolution rule free to
 * disagree — but this send has exactly ONE correct recipient, resolved and
 * returned with the draft, so it is shown literally.</p>
 *
 * <p>🔴 <b>No no-letter confirmation, and that is the ruling rather than an
 * omission.</b> The gate exists to stop Phoenix presenting an unqualified buyer
 * to a COUNTERPARTY. Showing a buyer their own package presents them to
 * nobody.</p>
 */
function SendPackageToBuyerModal({
  submissionId,
  onClose,
  onMarkSentExternally,
}: {
  submissionId: string;
  onClose: () => void;
  onMarkSentExternally: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const identity = useSenderIdentity();
  const [overrides, setOverrides] = useState<{ subject?: string; body?: string }>({});
  // Q1 — the one confirmation this send can ask for: a document 3 still in the
  // older format. Passed to the mutation explicitly, never read from state.
  const [olderFormatRefusal, setOlderFormatRefusal] = useState<string | null>(null);

  const draft = useQuery({
    queryKey: buyerSubmissionKeys.packageBuyerDraft(submissionId),
    queryFn: () => buyerSubmissionsApi.getPackageBuyerDraft(submissionId),
  });

  const send = useMutation({
    mutationFn: (confirmOlderFormat: boolean) =>
      buyerSubmissionsApi.sendPackageToBuyer(submissionId, {
        subject: overrides.subject ?? null,
        body: overrides.body ?? null,
        includeKinds: selection.includeKinds,
        confirmOlderFormatDocument: confirmOlderFormat,
      }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: buyerSubmissionKeys.all });
      setOlderFormatRefusal(null);
      toast.success(`Sent to ${r.toEmail}.`);
      onClose();
    },
    onError: (e: Error) => {
      const older = olderFormatRefusalOf(e);
      if (older) {
        setOlderFormatRefusal(older);
        return;
      }
      setOlderFormatRefusal(null);
      toast.error(e.message);
    },
  });

  const subject = overrides.subject ?? draft.data?.subject ?? "";
  const body = overrides.body ?? draft.data?.body ?? "";
  const manifest = draft.data?.attachments;
  const selection = useAttachmentSelection(submissionId, manifest, toast.error);
  const dirty = overrides.subject !== undefined || overrides.body !== undefined;

  // No recipient gate here: this send has exactly one correct address, and the
  // panel refuses to open the modal at all when the buyer has none.
  const blockedReason = selection.blockedReason;

  const replacesPreviewBody = draft.isLoading ? (
    <Skeleton className="h-48 w-full" />
  ) : draft.isError ? (
    <InlineQueryError
      what="the draft message"
      onRetry={() => void draft.refetch()}
      retrying={draft.isFetching}
    />
  ) : undefined;

  // 🔴 `notices`, NOT `replacesPreviewBody`. These two notes used to live in
  // the replacement slot, which swaps the message block out — and a fragment is
  // always truthy, so the coalesce never fell through and the subject, the body
  // and the attachment list were unreachable on this modal for the life of
  // #1136. The slot is named for what it does to its neighbours now.
  const notices = <MissingNote manifest={manifest} />;

  return (
    <>
      <SendPreviewModal
      title="Send the package to the buyer"
      onClose={onClose}
      toName={draft.data?.toName}
      toEmail={draft.data?.toEmail}
      // Continues the sentence after a comma — `Recipient` renders
      // ", {note}" — so it starts lower-case, like the intro email's.
      audienceNote="under your name. Sending it to the listing broker is a separate action."
      identity={identity}
      replacesPreviewBody={replacesPreviewBody}
      notices={notices}
      subject={subject}
      onSubjectChange={(v) => setOverrides({ ...overrides, subject: v })}
      body={body}
      onBodyChange={(v) => setOverrides({ ...overrides, body: v })}
      messageActions={
        <MessageActions
          dirty={dirty}
          onReset={() => setOverrides({})}
          subject={subject}
          body={body}
          roleStatement={draft.data?.roleStatement}
        />
      }
      roleStatement={draft.data?.roleStatement}
      selectableAttachments={selection.rows}
      carriedOverNote={CARRIED_OVER_NOTE}
      footnote={
        <>
          These are the same documents the listing broker receives. Sending them
          records this send on its own; it does not mark the package as sent to
          the broker and it does not change the submission&apos;s status.
        </>
      }
      secondaryAction={
        <Button
          variant="secondary"
          onClick={() => void downloadAll(submissionId, manifest, toast.error)}
        >
          Download
        </Button>
      }
      alternateAction={
        <Button variant="text" onClick={onMarkSentExternally}>
          I sent it myself
        </Button>
      }
      confirmLabel="Send from Phoenix"
      confirmTestId="send-package-to-buyer-confirm"
      onConfirm={() => send.mutate(false)}
      confirmDisabled={draft.isLoading || blockedReason !== null}
      confirmBlockedReason={blockedReason}
      pending={send.isPending}
    />
      {olderFormatRefusal ? (
        <ConfirmModal
          open
          title={OLDER_FORMAT_CONFIRM_TITLE}
          message={olderFormatRefusal}
          confirmLabel={OLDER_FORMAT_CONFIRM_LABEL}
          cancelLabel={OLDER_FORMAT_CANCEL_LABEL}
          onConfirm={() => {
            setOlderFormatRefusal(null);
            send.mutate(true);
          }}
          onCancel={() => setOlderFormatRefusal(null)}
        />
      ) : null}
    </>
  );
}

/**
 * 🔴 The refusal that means "the server is asking you to decide", told apart
 * from every other 400 by its CODE.
 *
 * <p>`ApiError.details` is the only structured channel that survives a 400. The
 * message beside it is copy and will be reworded; matching on it would stop
 * matching silently, and what it would stop doing is opening the confirmation —
 * so a decision the server is asking for would start reading as a flat
 * failure.</p>
 */
function isNoLetterRefusal(e: unknown): boolean {
  if (!(e instanceof ApiError)) return false;
  const details = e.details as { code?: unknown } | null | undefined;
  return details?.code === NO_PREQUALIFICATION_LETTER_CODE;
}

/** Q1 — the older-format refusal, told apart by its CODE. Returns the
 *  server's sentence, which the confirmation shows as is. */
function olderFormatRefusalOf(e: unknown): string | null {
  if (!(e instanceof ApiError)) return null;
  const details = e.details as { code?: unknown } | null | undefined;
  return details?.code === OLDER_FORMAT_DOCUMENT_CODE ? e.message : null;
}

/**
 * Opens ONE document in a new tab, for Preview.
 *
 * <p>The endpoint is cookie-authenticated, so the blob has to be fetched rather
 * than linked — which means the `window.open` happens after an `await` and has
 * lost its gesture context. A browser tolerates that for a single call and
 * blocks a burst of them, which is why the multi-file path SAVES instead. See
 * <c>downloadAll</c>.</p>
 */
async function openDocument(
  submissionId: string,
  kind: BuyerSubmissionDocumentKind,
  onError: (message: string) => void,
) {
  try {
    const blob = await buyerSubmissionsApi.getDocumentPdf(submissionId, kind);
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank", "noopener");
    // Revoked on a timer rather than immediately: revoking before the new tab
    // has read the object URL leaves the specialist looking at a blank tab.
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (e) {
    onError(e instanceof Error ? e.message : "Could not open the document.");
  }
}

/**
 * Downloads every document that is actually going out.
 *
 * <p>🔴 <b>SAVES rather than opening tabs, and that is not a preference.</b>
 * `window.open` after an `await` has lost the user-gesture context, so a browser
 * blocks it — with one file that was survivable because the fetch was usually
 * warm, and with a package of them it means the specialist gets one tab, a
 * run of silent popup blocks and no error. An anchor click is not popup-blocked, and it is what the
 * button says it does. Per-document Preview still opens a tab, because there the
 * whole point is to look at it.</p>
 *
 * <p>🔴 Driven by the SERVER's manifest, so Download and Send agree about which
 * files exist.</p>
 */
async function downloadAll(
  submissionId: string,
  manifest: BuyerPackageAttachmentManifestDto | undefined,
  onError: (message: string) => void,
) {
  if (!manifest || manifest.attachments.length === 0) {
    onError("There is nothing to download yet — no document has been written.");
    return;
  }

  for (const a of manifest.attachments) {
    // The filename the LISTING BROKER would have received, so a specialist
    // sending from their own client attaches files named as Phoenix names them
    // — a folder of files called "download.pdf" is what a broker cannot tell
    // apart,
    // which is the whole reason FileNameFor leads with the document.
    await saveDocument(submissionId, a.kind, a.fileName, (message) =>
      onError(`${a.title}: ${message}`),
    );
  }
}

/**
 * ONE document, SAVED.
 *
 * <p>🔴 The single place an anchor download is built, shared by the card, the
 * per-row Download in the send picker, and the multi-file `downloadAll`. A
 * second copy is a second answer to "does this open a tab or save a file", and
 * the answer matters: `window.open` after an `await` has lost its gesture
 * context and is popup-blocked, which is why only Preview opens a tab.</p>
 */
async function saveDocument(
  submissionId: string,
  kind: BuyerSubmissionDocumentKind,
  fileName: string,
  onError: (message: string) => void,
  format: "pdf" | "docx" = "pdf",
) {
  try {
    const blob =
      format === "docx"
        ? await buyerSubmissionsApi.getDocumentDocx(submissionId, kind)
        : await buyerSubmissionsApi.getDocumentPdf(submissionId, kind);
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (e) {
    onError(e instanceof Error ? e.message : "Could not download the document.");
  }
}

export type { BuyerSubmissionPackageDto };
